/**
 * 导出数据为CSV文件
 * @param {Array} data - 要导出的数据数组
 * @param {String} filename - 文件名（不含扩展名）
 * @param {Object} columnMap - 列映射 {原始字段名: '显示名称'}
 */
export const exportToCSV = (data, filename = '导出数据', columnMap = {}) => {
  if (!data || data.length === 0) {
    return false;
  }

  try {
    // 如果没有提供列映射，使用第一条数据的键作为列名
    const firstRow = data[0];
    const headers = Object.keys(columnMap).length > 0 
      ? Object.values(columnMap)
      : Object.keys(firstRow);

    // 准备CSV数据
    const csvRows = [
      headers.join(','),
      ...data.map(row => {
        const values = Object.keys(columnMap).length > 0
          ? Object.keys(columnMap).map(key => {
              const value = row[key];
              // 处理包含逗号、引号或换行符的值
              if (value === null || value === undefined) return '""';
              const stringValue = String(value).replace(/"/g, '""');
              return `"${stringValue}"`;
            })
          : Object.values(row).map(value => {
              if (value === null || value === undefined) return '""';
              const stringValue = String(value).replace(/"/g, '""');
              return `"${stringValue}"`;
            });
        return values.join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    
    // 创建Blob并下载
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('导出失败：', error);
    return false;
  }
};

/** CSV 单元格转义（与 Excel 兼容） */
const csvCell = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  return `"${s.replace(/"/g, '""')}"`;
};

/**
 * 设备批量导入模板（列顺序须与后端 BatchImportDevices 一致）
 * 前端直接生成，避免依赖线上旧版 /api/admin/devices/template
 */
export const downloadDeviceImportTemplateCsv = () => {
  const headers = [
    '当前表号（必填）',
    '设备类型（必填，1=电表，2=水表，3=门锁）',
    '厂商表号（选填）',
    '设备MAC地址（选填）',
    '设备详细种类（选填，1=三相挂壁，2=三相导轨，3=单相挂壁，4=单相导轨）',
    '通讯类型（选填，0=4G，1=蓝牙，2=WIFI）',
    '设备状态（选填，0=离线/故障，1=在线/正常，默认1）',
    '最低充值金额（选填，默认0.00）',
    '管理费（选填，默认0.00）',
    '设备备注（选填）',
    'IMEI（选填）',
    '所属厂家（选填，如ZT；水表ZT仅填表号+IMEI时可留空，将自动补厂商表号与备注）',
  ];
  const exampleRow = ['示例表号001', '1', '', '', '', '', '', '', '', '', '', ''];
  const lines = [
    headers.map(csvCell).join(','),
    exampleRow.map(csvCell).join(','),
  ];
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'device_import_template.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 汇付账号批量导入模板（表头为接口字段名，与 POST /api/admin/huifu/accounts 体字段一致；第二行为示例）
 * cert_validity_type：0=非长期有效，1=长期有效；cert_begin_date/cert_end_date：yyyyMMdd；status：1=正常，0=禁用
 */
export const downloadHuifuAccountImportTemplateCsv = () => {
  const headers = [
    'user_id',
    'real_name',
    'phone',
    'id_card',
    'cert_validity_type',
    'cert_begin_date',
    'cert_end_date',
    'huifu_id',
    'status',
  ];
  const exampleRow = [
    '1',
    '张三',
    '13800138000',
    '110101199001011234',
    '0',
    '20200101',
    '20300101',
    '123456789012345678',
    '1',
  ];
  const lines = [headers.map(csvCell).join(','), exampleRow.map(csvCell).join(',')];
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'huifu_account_import_template.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};/**
 * 汇付银行卡批量导入模板（表头为接口字段名，与 POST /api/admin/huifu/bank-cards 体字段一致）
 * is_default：0=否，1=是；card_status：1=正常，0=已注销；prov_id/area_id 为行政区划代码（与页面下拉一致，如 310000、310100）
 */
export const downloadHuifuBankCardImportTemplateCsv = () => {
  const headers = [
    'user_id',
    'bank_name',
    'bank_account_name',
    'bank_account_no',
    'bank_code',
    'prov_id',
    'area_id',
    'is_default',
    'card_status',
  ];
  const exampleRow = [
    '1',
    '中国工商银行',
    '张三',
    '6222021234567890123',
    'ICBC',
    '310000',
    '310100',
    '1',
    '1',
  ];
  const lines = [headers.map(csvCell).join(','), exampleRow.map(csvCell).join(',')];
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'huifu_bank_card_import_template.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};